import { useRef, useState, useCallback } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError } from "../lib/api";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_MB = 4;

export function CoverDropzone({ value, onChange, testId = "cover-dropzone" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Please choose a JPG, PNG, WEBP or GIF"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_MB}MB`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange?.(abs);
      toast.success("Cover uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const clear = () => onChange?.("");

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className="relative rounded-xl border-2 border-dashed cursor-pointer overflow-hidden"
      style={{
        borderColor: dragOver ? "#E040FB" : "var(--border-soft)",
        background: dragOver ? "rgba(224,64,251,0.06)" : "#0b0b0f",
        minHeight: 220,
        transition: "border-color 150ms ease, background 150ms ease",
      }}
      data-testid={testId}
    >
      <input
        type="file"
        accept={ALLOWED.join(",")}
        ref={inputRef}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {value ? (
        <>
          <img src={value} alt="Cover preview" className="w-full h-56 object-cover" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="absolute top-2 right-2 rounded-full p-1.5 btn-press"
            style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}
            data-testid={`${testId}-clear`}
            title="Remove cover"
          >
            <X size={14} />
          </button>
        </>
      ) : uploading ? (
        <div className="flex flex-col items-center justify-center py-14" style={{ color: "var(--ink-soft)" }}>
          <Loader2 size={22} className="animate-spin mb-2 rainbow-text" />
          <span className="text-sm">Uploading cover…</span>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-14 px-4 text-center" style={{ color: "var(--ink-soft)" }}>
          <UploadCloud size={26} className="mb-2 rainbow-text" />
          <div className="text-sm"><span className="rainbow-text font-bold">Drop</span> your cover here or <span className="rainbow-text font-bold">click</span> to browse</div>
          <div className="text-xs mt-1" style={{ color: "var(--ink-muted)" }}>JPG · PNG · WEBP · GIF · up to {MAX_MB}MB</div>
        </div>
      )}
    </div>
  );
}
